import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
  PlayIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { loadCourtSessions } from '../../utils/courtSessions';

const COLUMN_DEFS = [
  { key: 'session_id', label: 'Session ID' },
  { key: 'case_number', label: 'Case Number' },
  { key: 'case_title', label: 'Case Title' },
  { key: 'recorded_at', label: 'Recording Date / Time' },
  { key: 'duration', label: 'Audio Duration' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

const STATUS_META = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-800 border-amber-200' },
  uploaded: { label: 'Uploaded', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const parseSessionDate = (recordedAt) => {
  if (!recordedAt) return null;
  const d = new Date(String(recordedAt).replace(',', ''));
  return Number.isNaN(d.getTime()) ? null : d;
};

const IconAction = ({ label, dark, children, to, onClick }) => {
  const className = `inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
    dark
      ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
      : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-matdash-primary'
  }`;
  if (to) {
    return (
      <Link to={to} title={label} aria-label={label} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className={className}>
      {children}
    </button>
  );
};

const AdminSessions = ({
  transcriptBasePath = '/admin/transcriptions',
  onStartRecording = null,
  showMediaActions = false,
}) => {
  const { user } = useAuth();
  const { dark } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));

  useEffect(() => {
    let cancelled = false;
    loadCourtSessions(user?.tenant_id)
      .then((rows) => {
        if (!cancelled) setSessions(rows);
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.tenant_id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sessions.filter((row) => {
      const matchesStatus = status === 'all' || row.status === status;
      const matchesQuery = !q
        || row.session_id.toLowerCase().includes(q)
        || row.case_number.toLowerCase().includes(q)
        || row.case_title.toLowerCase().includes(q);
      const sessionDate = parseSessionDate(row.recorded_at_iso || row.recorded_at);
      let matchesDate = true;
      if (dateFrom || dateTo) {
        if (!sessionDate) {
          matchesDate = false;
        } else {
          if (dateFrom) {
            const from = new Date(`${dateFrom}T00:00:00`);
            if (sessionDate < from) matchesDate = false;
          }
          if (dateTo) {
            const to = new Date(`${dateTo}T23:59:59`);
            if (sessionDate > to) matchesDate = false;
          }
        }
      }
      return matchesStatus && matchesQuery && matchesDate;
    });
  }, [sessions, query, status, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);
  const show = (key) => visibleKeys.includes(key);
  const activeFilterCount = [status !== 'all'].filter(Boolean).length;

  const inputCls = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const searchCls = `${inputCls} w-full`;
  const dateCls = `${inputCls} w-[150px] shrink-0`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-20 hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setStatus('all');
    setPage(1);
  };

  const toggleColumn = (key) => {
    setVisibleKeys((keys) => (
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    ));
  };

  const exportCsv = () => {
    const headers = ['Session ID', 'Case Number', 'Case Title', 'Recording Date / Time', 'Duration', 'Status'];
    const lines = filtered.map((row) =>
      [
        row.session_id,
        row.case_number,
        row.case_title,
        row.recorded_at,
        row.duration,
        STATUS_META[row.status]?.label || row.status,
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-sessions.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-sessions.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  return (
    <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
      <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
        <div className="relative w-full max-w-sm min-w-[180px] shrink">
          <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search session or case..."
            className={`${searchCls} pl-9 h-9 py-1.5`}
          />
        </div>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className={`${dateCls} h-9 py-1.5`}
          aria-label="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className={`${dateCls} h-9 py-1.5`}
          aria-label="To date"
        />

        <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Filters">
              <FunnelIcon className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className={dropdownPanel}>
              <div className={dropdownInner}>
                <div>
                  <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                  <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    className={`${inputCls} w-full`}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="uploaded">Uploaded</option>
                  </select>
                </div>
                <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                  Clear filters
                </button>
              </div>
            </div>
          </div>

          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Column visibility">
              <Cog6ToothIcon className="h-4 w-4" />
            </button>
            <div className={dropdownPanel}>
              <div className={`w-52 py-2 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                {COLUMN_DEFS.map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                      dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleKeys.includes(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 text-matdash-primary"
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="relative group">
            <button type="button" className={toolBtn} aria-label="Export">
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Export</span>
            </button>
            <div className={dropdownPanel}>
              <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  type="button"
                  onClick={exportCsv}
                  className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                >
                  Export as CSV
                </button>
                <button
                  type="button"
                  onClick={exportJson}
                  className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                >
                  Export as JSON
                </button>
              </div>
            </div>
          </div>

          {typeof onStartRecording === 'function' && (
            <button
              type="button"
              onClick={onStartRecording}
              className="inline-flex items-center justify-center h-9 px-3 rounded-lg bg-matdash-primary text-white text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <MicrophoneIcon className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">Start Recording</span>
            </button>
          )}
        </div>
      </div>

      <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
        {filtered.length} sessions
      </div>

      <div className="overflow-x-auto">
        <table className="md-table min-w-full">
          <thead>
            <tr>
              {show('session_id') && <th>Session ID</th>}
              {show('case_number') && <th>Case Number</th>}
              {show('case_title') && <th>Case Title</th>}
              {show('recorded_at') && <th>Recording Date / Time</th>}
              {show('duration') && <th>Audio Duration</th>}
              {show('status') && <th>Status</th>}
              {show('actions') && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                  No sessions match your filters.
                </td>
              </tr>
            )}
            {pageRows.map((row) => {
              const meta = STATUS_META[row.status] || STATUS_META.pending;
              const isUploaded = row.status === 'uploaded';
              const transcriptId = row.transcription_id;
              return (
                <tr key={row.id}>
                  {show('session_id') && (
                    <td className="font-semibold whitespace-nowrap">{row.session_id}</td>
                  )}
                  {show('case_number') && (
                    <td className="text-matdash-muted whitespace-nowrap">{row.case_number}</td>
                  )}
                  {show('case_title') && (
                    <td className="min-w-[220px]">{row.case_title}</td>
                  )}
                  {show('recorded_at') && (
                    <td className="text-matdash-muted whitespace-nowrap">{row.recorded_at}</td>
                  )}
                  {show('duration') && (
                    <td className="text-matdash-muted whitespace-nowrap font-medium">{row.duration}</td>
                  )}
                  {show('status') && (
                    <td className="whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${meta.className}`}>
                        {meta.label}
                      </span>
                    </td>
                  )}
                  {show('actions') && (
                    <td>
                      <div className="flex items-center gap-1.5">
                        {showMediaActions && (
                          <IconAction label="Play recording" dark={dark}>
                            <PlayIcon className="h-4 w-4 text-matdash-primary" />
                          </IconAction>
                        )}
                        {row.status === 'pending' && (
                          <IconAction
                            label="Upload to transcribe"
                            dark={dark}
                            onClick={() =>
                              toast.message('Upload to transcribe', {
                                description: `${row.session_id} is ready to send for transcription.`,
                              })
                            }
                          >
                            <CloudArrowUpIcon className="h-4 w-4 text-matdash-primary" />
                          </IconAction>
                        )}
                        {isUploaded && transcriptId && (
                          <IconAction
                            label="View transcript"
                            dark={dark}
                            to={`${transcriptBasePath}/${transcriptId}`}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </IconAction>
                        )}
                        {showMediaActions && isUploaded && (
                          <IconAction label="Download" dark={dark}>
                            <DocumentArrowDownIcon className="h-4 w-4" />
                          </IconAction>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
        <p className="text-xs text-matdash-muted mb-0">
          Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} sessions
        </p>
        <div className="flex items-center gap-2">
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
          >
            {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
          <span className="text-xs text-matdash-muted px-1">Page {page} of {totalPages}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
        </div>
      </div>
    </div>
  );
};

export default AdminSessions;
